import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Box,
  Grid,
  Tooltip,
  Drawer,
  Divider,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Assessment as AssessmentIcon,
  ViewList as ViewListIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import api from "../api/axiosConfig";
import "./TeamManagement.css";

// Safe role formatter that handles both missing roles AND invalid role values
const safeFormatRole = (role) => {
  if (!role || typeof role !== "string") {
    return "Not Assigned";
  }

  // List of valid role patterns (with underscores)
  const validRoles = [
    "zonal_head",
    "team_lead",
    "branch_manager",
    "relationship_manager",
    "director",
    "admin",
    "user",
  ];

  // If it's a valid role with underscores, format it
  if (validRoles.includes(role)) {
    return role.replace(/_/g, " ");
  }

  // If it's a name or invalid value, show a default
  return "Team Member";
};

// ====== NEW HELPER API FUNCTIONS (patched) ======
// Fetch aggregated performance for all teams
async function fetchAllTeamsPerformance(apiBaseUrl, token, startDate, endDate) {
  const url = `${apiBaseUrl}/api/teams/performance-data/all?startDate=${startDate}&endDate=${endDate}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok)
    throw new Error("Failed to fetch all teams performance: " + resp.status);
  return resp.json();
}

// Fetch performance for a single team (returns members + performanceData)
async function fetchTeamPerformance(
  apiBaseUrl,
  token,
  teamId,
  startDate,
  endDate
) {
  const url = `${apiBaseUrl}/api/teams/${teamId}/performance-data?startDate=${startDate}&endDate=${endDate}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok)
    throw new Error("Failed to fetch team performance: " + resp.status);
  return resp.json();
}
// ====== END HELPERS ======
const TeamManagement = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [createTeamDialog, setCreateTeamDialog] = useState(false);
  const [editTeamDialog, setEditTeamDialog] = useState(false);
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [viewMembersDialog, setViewMembersDialog] = useState(false);

  // Form states
  const [teamForm, setTeamForm] = useState({ team_name: "", description: "" });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage, default to 'all' to avoid heavy performance queries on page load
    return localStorage.getItem("teamManagementViewMode") || "all";
  });
  const [teamsByReports, setTeamsByReports] = useState({});
  const [performanceData, setPerformanceData] = useState([]);
  const [selectedTeamForPerformance, setSelectedTeamForPerformance] =
    useState(null);
  const [dateRange, setDateRange] = useState("30"); // Default to 30 days
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // Individual member performance states
  const [memberPerformanceData, setMemberPerformanceData] = useState([]);
  const [selectedMemberFilter, setSelectedMemberFilter] = useState("all");
  const [memberDateRange, setMemberDateRange] = useState("30"); // Default to 30 days
  const [customMemberStartDate, setCustomMemberStartDate] = useState(null);
  const [customMemberEndDate, setCustomMemberEndDate] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  // Display mode states
  const [displayMode, setDisplayMode] = useState("count"); // 'count' or 'points'

  // Point values for metrics used in calculations
  const pointValues = {
    telephoneSalesCalls: 1,
    faceToFace: 1,
    appointmentMade: 2,
    appointmentKept: 2,
    factFind: 2,
    serviceReviews: 1,
    socialCalls: 1,
    highIncomeWealthy: 2,
    referrals: 3,
    closingInterview: 4,
    salesClose: 5,
    totalPoints: 24,
  };

  // Mapping human labels → metric keys
  const metricLabelToKey = {
    "Telephone Sales Calls": "telephoneSalesCalls",
    "Face to Face": "faceToFace",
    "Appointment Made": "appointmentMade",
    "Appointment Kept": "appointmentKept",
    "Fact Find": "factFind",
    "Service Reviews": "serviceReviews",
    "Social Calls": "socialCalls",
    "High Income Wealthy": "highIncomeWealthy",
    Referrals: "referrals",
    "Closing Interview": "closingInterview",
    "Sales Close": "salesClose",
  };

  // Helper function to get display value based on mode
  const getDisplayValue = (metricKey, count) => {
    if (displayMode === "count") {
      return count || 0;
    } else {
      // Points mode: multiply count by point value
      const pointValue = pointValues[metricKey] || 1;
      return (count || 0) * pointValue;
    }
  };

  // Helper function to calculate days between dates
  const calculateDays = (dateRangeObj) => {
    if (!dateRangeObj || !dateRangeObj.startDate || !dateRangeObj.endDate)
      return 7; // Default for team performance
    const start = new Date(dateRangeObj.startDate);
    const end = new Date(dateRangeObj.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      setError("Authentication failed - please login as admin");
      setLoading(false);
      return;
    }
    fetchTeams();
    fetchAvailableUsers();
  }, []);

  // Retry mechanism for failed API calls
  const fetchWithRetry = async (apiCall, retries = 2, delay = 1000) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const result = await apiCall();
        return result;
      } catch (err) {
        console.warn(
          `API call failed (attempt ${i + 1}/${retries + 1}):`,
          err.message
        );
        if (i < retries) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay *= 2; // Exponential backoff
        } else {
          throw err; // Re-throw after all retries exhausted
        }
      }
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetchWithRetry(() =>
        api.get("/api/teams", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        })
      );
      setTeams(response.data);
    } catch (err) {
      setError("Failed to fetch teams after multiple attempts");
      console.error("Error fetching teams:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await fetchWithRetry(() =>
        api.get("/api/teams/available-users", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        })
      );
      setAvailableUsers(response.data);
    } catch (err) {
      console.error("Error fetching available users:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch available users",
        severity: "error",
      });
    }
  };

  const handleCreateTeam = async () => {
    try {
      await api.post("/api/teams", teamForm, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setSnackbar({
        open: true,
        message: "Team created successfully",
        severity: "success",
      });
      setCreateTeamDialog(false);
      setTeamForm({ team_name: "", description: "" });
      fetchTeams();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to create team",
        severity: "error",
      });
    }
  };

  const handleEditTeam = async () => {
    try {
      await api.put(`/api/teams/${selectedTeam.team_id}`, teamForm, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setSnackbar({
        open: true,
        message: "Team updated successfully",
        severity: "success",
      });
      setEditTeamDialog(false);
      setTeamForm({ team_name: "", description: "" });
      setSelectedTeam(null);
      fetchTeams();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to update team",
        severity: "error",
      });
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this team? This will remove all team members."
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/teams/${teamId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setSnackbar({
        open: true,
        message: "Team deleted successfully",
        severity: "success",
      });
      fetchTeams();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to delete team",
        severity: "error",
      });
    }
  };

  const handleAddMembers = async () => {
    try {
      for (const userId of selectedUsers) {
        await api.post(
          `/api/teams/${selectedTeam.team_id}/members`,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );
      }
      setSnackbar({
        open: true,
        message: "Members added successfully",
        severity: "success",
      });
      setAddMemberDialog(false);
      setSelectedUsers([]);
      setSelectedTeam(null);
      fetchTeams();
      fetchAvailableUsers();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to add members",
        severity: "error",
      });
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this member from the team?"
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/teams/${teamId}/members/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      setSnackbar({
        open: true,
        message: "Member removed successfully",
        severity: "success",
      });

      // Update the selectedTeam members list locally to remove the deleted member
      if (selectedTeam && selectedTeam.members) {
        setSelectedTeam({
          ...selectedTeam,
          members: selectedTeam.members.filter(
            (member) => member.user_id !== userId
          ),
        });
      }

      fetchTeams(); // Refresh teams list to update member count
      fetchAvailableUsers(); // Refresh available users list
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to remove member",
        severity: "error",
      });
    }
  };

  const openEditDialog = (team) => {
    setSelectedTeam(team);
    setTeamForm({
      team_name: team.team_name,
      description: team.description || "",
    });
    setEditTeamDialog(true);
  };

  const openAddMemberDialog = (team) => {
    setSelectedTeam(team);
    setAddMemberDialog(true);
  };

  const openViewMembersDialog = async (team) => {
    try {
      const response = await api.get(`/api/teams/${team.team_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      setSelectedTeam(response.data);
      setViewMembersDialog(true);
    } catch (err) {
      console.error("Error fetching team members:", err);
      setSnackbar({
        open: true,
        message: "Failed to load team members",
        severity: "error",
      });
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Group teams by reporting structure
  const organizeTeamsByReports = async () => {
    try {
      // Fetch detailed team information with members
      const detailedTeams = await Promise.all(
        teams.map(async (team) => {
          try {
            const response = await api.get(`/api/teams/${team.team_id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
              },
            });
            return response.data;
          } catch (err) {
            console.error(`Error fetching team ${team.team_id}:`, err);
            return team; // Return basic team info if detailed fetch fails
          }
        })
      );

      // Group teams by their reporting hierarchy
      const groupedTeams = {};

      detailedTeams.forEach((team) => {
        if (team.members && team.members.length > 0) {
          // Find the highest-level manager in the team
          const managers = team.members.filter((member) =>
            ["director", "zonal_head", "branch_manager", "team_lead"].includes(
              member.role
            )
          );

          if (managers.length > 0) {
            // Sort by hierarchy level (director > zonal_head > branch_manager > team_lead)
            const hierarchyOrder = {
              director: 1,
              zonal_head: 2,
              branch_manager: 3,
              team_lead: 4,
            };
            managers.sort(
              (a, b) => hierarchyOrder[a.role] - hierarchyOrder[b.role]
            );

            const primaryManager = managers[0];
            const managerKey = `${primaryManager.role
              .replace(/_/g, " ")
              .toUpperCase()}: ${primaryManager.name}`;

            if (!groupedTeams[managerKey]) {
              groupedTeams[managerKey] = {
                manager: primaryManager,
                teams: [],
              };
            }
            groupedTeams[managerKey].teams.push(team);
          } else {
            // No managers found, group under "Unassigned Teams"
            if (!groupedTeams["UNASSIGNED TEAMS"]) {
              groupedTeams["UNASSIGNED TEAMS"] = {
                manager: null,
                teams: [],
              };
            }
            groupedTeams["UNASSIGNED TEAMS"].teams.push(team);
          }
        } else {
          // Empty teams
          if (!groupedTeams["EMPTY TEAMS"]) {
            groupedTeams["EMPTY TEAMS"] = {
              manager: null,
              teams: [],
            };
          }
          groupedTeams["EMPTY TEAMS"].teams.push(team);
        }
      });

      setTeamsByReports(groupedTeams);
    } catch (err) {
      console.error("Error organizing teams by reports:", err);
      setSnackbar({
        open: true,
        message: "Failed to organize teams by reports",
        severity: "error",
      });
    }
  };

  // Effect to organize teams when view mode changes or teams data updates
  useEffect(() => {
    if (viewMode === "by-reports" && teams.length > 0) {
      organizeTeamsByReports();
    }
  }, [viewMode, teams]);

  // Effect to save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("teamManagementViewMode", viewMode);
  }, [viewMode]);

  // Fetch team performance data by day
  const fetchTeamPerformanceByDay = async (teamId, startDate, endDate) => {
    try {
      let apiUrl;
      let params = {};

      // Validate dates
      if (!startDate || !endDate) {
        console.error("Missing date parameters");
        setPerformanceData([]);
        return;
      }

      if (teamId === "all") {
        apiUrl = "/api/teams/performance-data/all";
        params = {
          startDate: startDate.toString(),
          endDate: endDate.toString(),
        };
      } else {
        apiUrl = `/api/teams/${teamId}/performance-data`;
        params = {
          startDate: startDate.toString(),
          endDate: endDate.toString(),
        };
      }

      const response = await api.get(apiUrl, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        timeout: 30000, // 30 second timeout
      });

      // Handle the response based on structure
      if (teamId === "all") {
        if (response.data.teams && Array.isArray(response.data.teams)) {
          // Process all teams data
          const aggregatedData = aggregateAllTeamsData(response.data.teams);
          setPerformanceData(aggregatedData);
        } else {
          console.warn(
            "Unexpected response structure for all teams:",
            response.data
          );
          setPerformanceData([]);
        }
      } else {
        // Single team response
        const teamData =
          response.data.performanceData ||
          [].filter(
            (row) => row.metricKey !== "newLeads" && row.metric !== "New Leads"
          );
        setPerformanceData(teamData);
      }
    } catch (err) {
      console.error("Error fetching team performance by day:", err);
      console.error("Error details:", err.response?.data || err.message);

      setSnackbar({
        open: true,
        message: `Failed to fetch performance data: ${
          err.response?.data?.message || err.message
        }`,
        severity: "error",
      });
      setPerformanceData([]);
    }
  };

  // Helper function to aggregate data from all teams
  const aggregateAllTeamsData = (teams) => {
    const metricsMap = {};
    const daysOfWeek = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    teams.forEach((team) => {
      if (team.members && Array.isArray(team.members)) {
        team.members.forEach((member) => {
          if (member.performance && Array.isArray(member.performance)) {
            member.performance.forEach((perfData) => {
              const entryDate = new Date(perfData.entryDate);
              const dayOfWeek = daysOfWeek[entryDate.getDay()];

              const metrics = [
                { key: "telephoneSalesCalls", label: "Telephone Sales Calls" },
                { key: "faceToFace", label: "Face to Face" },
                { key: "appointmentMade", label: "Appointment Scheduled" },
                { key: "appointmentKept", label: "Appointment Attended" },
                { key: "factFind", label: "Fact Find" },
                { key: "serviceReviews", label: "Service Reviews" },
                { key: "socialCalls", label: "Social Calls" },
                { key: "highIncomeWealthy", label: "High Income/Wealthy" },
                { key: "referrals", label: "Referrals" },
                { key: "closingInterview", label: "Closing Interview" },
                { key: "salesClose", label: "Sales Close" },
              ];

              metrics.forEach(({ key, label }) => {
                if (!metricsMap[key]) {
                  metricsMap[key] = {
                    metricKey: key,
                    metric: label,
                    Monday: 0,
                    Tuesday: 0,
                    Wednesday: 0,
                    Thursday: 0,
                    Friday: 0,
                    Saturday: 0,
                    Sunday: 0,
                    total: 0,
                  };
                }

                const value = perfData[key] || 0;
                metricsMap[key][dayOfWeek] += value;
                metricsMap[key].total += value;
              });
            });
          }
        });
      }
    });

    return Object.values(metricsMap);
  };

  // Fetch team members
  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await api.get(`/api/teams/${teamId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      const members = response.data.members || [];
      setTeamMembers(members);
      return members;
    } catch (err) {
      console.error("Error fetching team members:", err);
      setTeamMembers([]);
      return [];
    }
  };

  const fetchMemberPerformanceByDay = async (userId, startDate, endDate) => {
    try {
      const response = await api.get(
        `/api/users/${userId}/performance-by-day?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      const raw = response.data.performanceData || [];

      const fixedRows = raw
        .map((row) => ({
          ...row,
          metricKey:
            metricLabelToKey[row.metric] || row.metricKey || row.metric,
        }))
        .filter(
          (row) => row.metricKey !== "newLeads" && row.metric !== "New Leads"
        );

      setMemberPerformanceData(fixedRows);
    } catch (err) {
      console.error("Error fetching member performance by day:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch member performance data",
        severity: "error",
      });
      setMemberPerformanceData([]);
    }
  };

  const fetchTeamPerformance = async (teamId, start, end) => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/teams/${teamId}/performance-data`, {
        params: { startDate: start, endDate: end },
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (e) {
      console.error("Error fetching team performance:", e);
      return null;
    }
  };
  // Effect to fetch prformance data when filters change
  useEffect(() => {
    if (viewMode === "by-reports") {
      // Calculate date range in IST timezone to match database storage
      let startDateStr, endDate;

      if (dateRange === "custom") {
        if (!customStartDate || !customEndDate) {
          return; // Wait for custom dates to be selected
        }

        // Convert custom dates to IST timezone date strings
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const startIstTime = new Date(customStartDate.getTime() + istOffset);
        const endIstTime = new Date(customEndDate.getTime() + istOffset);

        startDateStr = startIstTime.toISOString().split("T")[0];
        endDate = endIstTime.toISOString().split("T")[0];
      } else {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const istTime = new Date(now.getTime() + istOffset);

        endDate = istTime.toISOString().split("T")[0];

        const startDate = new Date(istTime);
        startDate.setDate(startDate.getDate() - parseInt(dateRange));
        startDateStr = startDate.toISOString().split("T")[0];
      }

      const teamId = selectedTeamFilter === "all" ? "all" : selectedTeamFilter;
      console.log(
        "Fetching performance data for team:",
        teamId,
        "from:",
        startDateStr,
        "to:",
        endDate
      );
      fetchTeamPerformanceByDay(teamId, startDateStr, endDate);

      // Fetch team members when a specific team is selected
      if (selectedTeamFilter !== "all") {
        fetchTeamMembers(selectedTeamFilter);
        setSelectedMemberFilter("all"); // Reset member filter
      } else {
        setTeamMembers([]);
        setMemberPerformanceData([]);
      }
    }
  }, [viewMode, selectedTeamFilter, dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const valid =
      viewMode === "by-reports" &&
      selectedTeamFilter !== "all" &&
      selectedMemberFilter !== "all";

    if (!valid) return;

    let startDateStr, endDate;

    if (memberDateRange === "custom") {
      if (!customMemberStartDate || !customMemberEndDate) {
        return; // Wait for custom dates to be selected
      }

      // Convert custom dates to IST timezone date strings
      const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
      const startIstTime = new Date(
        customMemberStartDate.getTime() + istOffset
      );
      const endIstTime = new Date(customMemberEndDate.getTime() + istOffset);

      startDateStr = startIstTime.toISOString().split("T")[0];
      endDate = endIstTime.toISOString().split("T")[0];
    } else {
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istTime = new Date(now.getTime() + istOffset);

      endDate = istTime.toISOString().split("T")[0];

      const startDate = new Date(istTime);
      startDate.setDate(startDate.getDate() - parseInt(memberDateRange));
      startDateStr = startDate.toISOString().split("T")[0];
    }

    fetchMemberPerformanceByDay(selectedMemberFilter, startDateStr, endDate);
  }, [
    viewMode,
    selectedTeamFilter,
    selectedMemberFilter,
    memberDateRange,
    customMemberStartDate,
    customMemberEndDate,
  ]);

  // Download functions
  const downloadTeamPerformanceCSV = () => {
    if (performanceData.length === 0) return;

    const headers = [
      "Metrics",
      "Points",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
      "Total",
    ];
    const csvContent = [
      headers.join(","),
      ...performanceData.map((row) =>
        [
          row.metric === "telephoneSalesCalls"
            ? "Telephone Sales Calls"
            : row.metric === "faceToFace"
            ? "Face to Face"
            : row.metric === "appointmentMade"
            ? "Appointment Scheduled"
            : row.metric === "appointmentKept"
            ? "Appointment Attended"
            : row.metric === "factFind"
            ? "Fact Find"
            : row.metric === "serviceReviews"
            ? "Service Reviews"
            : row.metric === "socialCalls"
            ? "Social Calls"
            : row.metric === "highIncomeWealthy"
            ? "High Income/Wealthy"
            : row.metric === "referrals"
            ? "Referrals"
            : row.metric === "closingInterview"
            ? "Closing Interview"
            : row.metric === "salesClose"
            ? "Sales Close"
            : row.metric === "Total Points"
            ? "Total Points"
            : row.metric,
          row.metric === "Total Points"
            ? 24
            : pointValues[row.metricKey || row.metric] || 1,
          row.Monday || 0,
          row.Tuesday || 0,
          row.Wednesday || 0,
          row.Thursday || 0,
          row.Friday || 0,
          row.Saturday || 0,
          row.Sunday || 0,
          row.metric === "Total Points"
            ? (() => {
                const totalSum = performanceData
                  .filter((r) => r.metric !== "Total Points")
                  .reduce((sum, r) => sum + (r.total || 0), 0);
                return totalSum;
              })()
            : row.total || 0,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `team_performance_${
        selectedTeamFilter === "all"
          ? "all_teams"
          : "team_" + selectedTeamFilter
      }_${dateRange}_days.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadMemberPerformanceCSV = () => {
    if (memberPerformanceData.length === 0) return;

    const headers = [
      "Metrics",
      "Points",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
      "Total",
    ];
    const csvContent = [
      headers.join(","),
      ...memberPerformanceData.map((row) =>
        [
          row.metric === "telephoneSalesCalls"
            ? "Telephone Sales Calls"
            : row.metric === "faceToFace"
            ? "Face to Face"
            : row.metric === "appointmentMade"
            ? "Appointment Scheduled"
            : row.metric === "appointmentKept"
            ? "Appointment Attended"
            : row.metric === "factFind"
            ? "Fact Find"
            : row.metric === "serviceReviews"
            ? "Service Reviews"
            : row.metric === "socialCalls"
            ? "Social Calls"
            : row.metric === "highIncomeWealthy"
            ? "High Income/Wealthy"
            : row.metric === "referrals"
            ? "Referrals"
            : row.metric === "closingInterview"
            ? "Closing Interview"
            : row.metric === "salesClose"
            ? "Sales Close"
            : row.metric === "Total Points"
            ? "Total Points"
            : row.metric,
          row.metric === "Total Points"
            ? 24
            : (() => {
                const pointValues = {
                  telephoneSalesCalls: 1,
                  faceToFace: 1,
                  appointmentMade: 2,
                  appointmentKept: 2,
                  factFind: 2,
                  serviceReviews: 1,
                  socialCalls: 1,
                  highIncomeWealthy: 2,
                  referrals: 3,
                  closingInterview: 4,
                  salesClose: 5,
                };
                return pointValues[row.metric] || 24;
              })(),
          row.Monday || 0,
          row.Tuesday || 0,
          row.Wednesday || 0,
          row.Thursday || 0,
          row.Friday || 0,
          row.Saturday || 0,
          row.Sunday || 0,
          row.metric === "Total Points"
            ? (() => {
                const totalSum = memberPerformanceData
                  .filter((r) => r.metric !== "Total Points")
                  .reduce((sum, r) => sum + (r.total || 0), 0);
                return totalSum;
              })()
            : row.total || 0,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const memberName =
      teamMembers.find((m) => m.user_id.toString() === selectedMemberFilter)
        ?.name || "member";
    link.setAttribute(
      "download",
      `member_performance_${memberName}_${memberDateRange}_days.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download individual member performance data as Excel (weekly format)
  const downloadMemberPerformanceExcel = async () => {
    try {
      if (selectedMemberFilter === "all") {
        setSnackbar({
          open: true,
          message:
            "Please select a specific team member to download their performance data",
          severity: "warning",
        });
        return;
      }

      if (memberPerformanceData.length === 0) {
        setSnackbar({
          open: true,
          message: "No performance data available for the selected member",
          severity: "warning",
        });
        return;
      }

      // Calculate date range in IST timezone to match database storage
      let startDateStr, endDateStr;

      if (memberDateRange === "custom") {
        if (!customMemberStartDate || !customMemberEndDate) {
          setSnackbar({
            open: true,
            message: "Please select both start and end dates for custom range",
            severity: "warning",
          });
          return;
        }

        if (
          isNaN(customMemberStartDate.getTime()) ||
          isNaN(customMemberEndDate.getTime())
        ) {
          setSnackbar({
            open: true,
            message: "Invalid custom date range selected",
            severity: "error",
          });
          return;
        }

        if (customMemberStartDate > customMemberEndDate) {
          setSnackbar({
            open: true,
            message: "Start date cannot be after end date",
            severity: "warning",
          });
          return;
        }

        // Convert custom dates to IST timezone date strings
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const startIstTime = new Date(
          customMemberStartDate.getTime() + istOffset
        );
        const endIstTime = new Date(customMemberEndDate.getTime() + istOffset);

        startDateStr = startIstTime.toISOString().split("T")[0];
        endDateStr = endIstTime.toISOString().split("T")[0];
      } else {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const istTime = new Date(now.getTime() + istOffset);

        endDateStr = istTime.toISOString().split("T")[0];

        const startDateObj = new Date(istTime);
        startDateObj.setDate(
          startDateObj.getDate() - parseInt(memberDateRange)
        );
        startDateStr = startDateObj.toISOString().split("T")[0];
      }

      const formatFullDate = (dateValue) => {
        if (!dateValue) return "";
        const parsed = new Date(dateValue);
        if (Number.isNaN(parsed.getTime())) {
          console.warn("Invalid date in formatFullDate:", dateValue);
          return "";
        }
        return parsed.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      };

      // Fetch raw performance data for the member to create weekly format
      const response = await api.get(
        `/api/users/${selectedMemberFilter}/performance-by-day?daily=true&startDate=${startDateStr}&endDate=${endDateStr}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      const dailyPerformance = response.data.members?.[0]?.performance || [];
      const leavePeriods = response.data.members?.[0]?.leavePeriods || [];

      // Helper function to check if a date falls within any leave period
      const isDateOnLeave = (date) => {
        if (!leavePeriods || leavePeriods.length === 0) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return leavePeriods.some((period) => {
          const start = new Date(period.startDate);
          const end = period.endDate ? new Date(period.endDate) : new Date();
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return checkDate >= start && checkDate <= end;
        });
      };

      // Build member metrics and structures (reuse from team download)
      const memberMetrics = [
        {
          metric: "telephoneSalesCalls",
          displayName: "Telephone Sales Calls",
          points: 1,
        },
        { metric: "faceToFace", displayName: "Face to Face", points: 1 },
        {
          metric: "appointmentMade",
          displayName: "Appointment Scheduled",
          points: 2,
        },
        {
          metric: "appointmentKept",
          displayName: "Appointment Attended",
          points: 2,
        },
        { metric: "factFind", displayName: "Fact Find", points: 2 },
        {
          metric: "serviceReviews",
          displayName: "Service Reviews",
          points: 1,
        },
        { metric: "socialCalls", displayName: "Social Calls", points: 1 },
        {
          metric: "highIncomeWealthy",
          displayName: "High Income/Wealthy",
          points: 2,
        },
        { metric: "referrals", displayName: "Referrals", points: 3 },
        {
          metric: "closingInterview",
          displayName: "Closing Interview",
          points: 4,
        },
        { metric: "salesClose", displayName: "Sales Close", points: 5 },
      ];

      const weekdayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];

      // ===== UPDATED: Weeks always start on Monday =====
      // Week ranges are aligned to calendar weeks (Monday to Sunday)
      const buildWeekRanges = (start, end) => {
        const ranges = [];
        if (!start || !end) return ranges;
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        if (
          Number.isNaN(startDateObj.getTime()) ||
          Number.isNaN(endDateObj.getTime())
        ) {
          return ranges;
        }
        startDateObj.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);

        // Find the Monday of the week containing the start date
        let cursor = new Date(startDateObj);
        const dayOfWeek = cursor.getDay(); // 0 = Sunday, 1 = Monday, etc.
        // Calculate days to go back to get to Monday
        // If Sunday (0), go back 6 days; if Monday (1), go back 0 days; if Tuesday (2), go back 1 day, etc.
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        cursor.setDate(cursor.getDate() - daysToMonday);

        while (cursor <= endDateObj) {
          const weekStart = new Date(cursor);

          // Week always ends on Sunday (6 days after Monday)
          let weekEnd = new Date(cursor);
          weekEnd.setDate(weekEnd.getDate() + 6);

          // Clamp to the actual requested date range for display
          const displayStart = weekStart < startDateObj ? startDateObj : weekStart;
          const displayEnd = weekEnd > endDateObj ? endDateObj : weekEnd;

          ranges.push({ 
            start: new Date(displayStart), 
            end: new Date(displayEnd),
            // Also store the full week boundaries for proper day mapping
            weekMonday: new Date(weekStart),
            weekSunday: new Date(weekEnd)
          });

          // Move cursor to next Monday
          cursor.setDate(cursor.getDate() + 7);
        }
        return ranges;
      };
      // ===== END UPDATED =====

      const buildMemberWeekTables = (member, effectiveWeeks) => {
        const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        const weeklyHeader = ["TASK", "PTS", "MON", "TUES", "WED", "THURS", "FRI", "SAT", "SUN", "WK TOTAL"];
        
        const weekTables = effectiveWeeks.map((week, index) => {
          const totals = {};
          const leaveDays = {}; // Track which days are on leave
          const dataAvailable = {}; // Track which days have data in the selected range
          
          memberMetrics.forEach((metric) => {
            totals[metric.metric] = {
              Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0,
              Friday: 0, Saturday: 0, Sunday: 0,
            };
          });

          // Use the actual calendar week (weekMonday to weekSunday) for day mapping
          const weekMonday = week.weekMonday || week.start;
          const weekSunday = week.weekSunday || week.end;
          
          // Check each day of the calendar week for leave status and data availability
          let cursor = new Date(weekMonday);
          while (cursor <= weekSunday) {
            const dayName = weekdayNames[cursor.getDay()];
            if (dayName) {
              // Check if this date is within the user's selected range
              const isInRange = cursor >= week.start && cursor <= week.end;
              dataAvailable[dayName] = isInRange;
              
              if (isDateOnLeave(cursor)) {
                leaveDays[dayName] = true;
              }
            }
            cursor.setDate(cursor.getDate() + 1);
          }

          // Populate performance data
          (member.performance || []).forEach((entry) => {
            const entryDateValue = entry.entryDate || entry.entry_date;
            if (!entryDateValue) return;
            const entryDate = new Date(entryDateValue);
            if (
              Number.isNaN(entryDate.getTime()) ||
              entryDate < week.start ||
              entryDate > week.end
            ) {
              return;
            }
            const dayName = weekdayNames[entryDate.getDay()];
            if (!dayName) return;
            memberMetrics.forEach((metric) => {
              const value = Number(entry[metric.metric]) || 0;
              totals[metric.metric][dayName] += value;
            });
          });

          const rows = [];
          rows.push([...weeklyHeader]);
          
          memberMetrics.forEach((metric) => {
            const metricTotals = totals[metric.metric];
            // Calculate week total (only counting non-leave days that are in range)
            const weekTotal = dayOrder.reduce(
              (sum, day) => sum + (leaveDays[day] || !dataAvailable[day] ? 0 : (metricTotals[day] || 0)),
              0
            );
            rows.push([
              metric.displayName,
              metric.points,
              ...dayOrder.map((day) => {
                if (!dataAvailable[day]) return "-"; // Day not in selected range
                if (leaveDays[day]) return "Leave";
                return metricTotals[day] || 0;
              }),
              weekTotal,
            ]);
          });

          // Total Points row
          const totalPointsByDay = dayOrder.map((day) => {
            if (!dataAvailable[day]) return "-";
            if (leaveDays[day]) return "Leave";
            return memberMetrics.reduce(
              (sum, metric) =>
                sum + (totals[metric.metric][day] || 0) * (metric.points || 0),
              0
            );
          });
          // Calculate memberWeekTotal excluding leave days and days not in range
          const memberWeekTotal = dayOrder.reduce(
            (sum, day, idx) => {
              if (!dataAvailable[day] || leaveDays[day]) return sum;
              const val = totalPointsByDay[idx];
              return sum + (typeof val === 'number' ? val : 0);
            },
            0
          );
          rows.push(["Total Points", "", ...totalPointsByDay, memberWeekTotal]);

          return {
            label: `Week ${index + 1} (${formatFullDate(
              week.start
            )} - ${formatFullDate(week.end)})`,
            rows,
          };
        });

        const weekLabelRow = [];
        weekTables.forEach((table, index) => {
          weekLabelRow.push(table.label);
          for (let i = 1; i < weeklyHeader.length; i += 1) {
            weekLabelRow.push("");
          }
          if (index < weekTables.length - 1) {
            const gapWidth = 2;
            weekLabelRow.push(...new Array(gapWidth).fill(""));
          }
        });

        const tableHeight = weekTables[0]?.rows.length || 0;
        const combinedRows = [];
        for (let rowIndex = 0; rowIndex < tableHeight; rowIndex += 1) {
          const row = [];
          weekTables.forEach((table, tableIndex) => {
            row.push(...table.rows[rowIndex]);
            if (tableIndex < weekTables.length - 1) {
              const gapWidth = 2;
              row.push(...new Array(gapWidth).fill(""));
            }
          });
          combinedRows.push(row);
        }

        return { weekLabelRow, tableRows: combinedRows };
      };

      const appendMemberWeeklyTables = (
        sheetRows,
        member,
        effectiveWeeks,
        fallbackStart,
        fallbackEnd
      ) => {
        const resolvedWeeks =
          effectiveWeeks.length > 0
            ? effectiveWeeks
            : buildWeekRanges(fallbackStart, fallbackEnd);
        const { weekLabelRow, tableRows } = buildMemberWeekTables(
          member,
          resolvedWeeks
        );
        const memberId = member.user_id || member.userId || member.id || "-";
        sheetRows.push([
          `User: ${member.name || "Unknown"} (User ID: ${memberId})`,
        ]);
        sheetRows.push([]);
        sheetRows.push(weekLabelRow);
        tableRows.forEach((row) => sheetRows.push(row));
        sheetRows.push([]);
      };

      // Get member details for filename
      const memberName =
        teamMembers.find((m) => m.user_id.toString() === selectedMemberFilter)
          ?.name || "member";

      const computedWeeks = buildWeekRanges(startDateStr, endDateStr);
      const weeksToUse =
        computedWeeks.length > 0
          ? computedWeeks
          : buildWeekRanges(startDateStr, endDateStr);

      // Create member data structure similar to team data structure
      const memberData = {
        name: memberName,
        user_id: selectedMemberFilter,
        performance: dailyPerformance,
      };

      const sheetRows = [];
      appendMemberWeeklyTables(
        sheetRows,
        memberData,
        weeksToUse,
        startDateStr,
        endDateStr
      );

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(sheetRows);
      XLSX.utils.book_append_sheet(workbook, ws, memberName.substring(0, 31));

      // Generate Excel file
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${memberName}_performance_${startDateStr}_to_${endDateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Member Excel file downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading member performance Excel:", error);
      setSnackbar({
        open: true,
        message: "Failed to download Member Excel file",
        severity: "error",
      });
    }
  };

  // Download team members performance data as Excel
  const downloadTeamMembersExcel = async (team) => {
    try {
      const response = await api.get(
        `/api/teams/${team.team_id}/performance-data`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      const { members, dateRange } = response.data;

      if (members.length === 0) {
        setSnackbar({
          open: true,
          message: "No performance data available for this team",
          severity: "warning",
        });
        return;
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Create a sheet for each member
      members.forEach((member) => {
        const memberData = member.performance;
        const leavePeriods = member.leavePeriods || [];

        // Helper function to check if a date falls within any leave period
        const isDateOnLeave = (date) => {
          if (!leavePeriods || leavePeriods.length === 0) return false;
          const checkDate = new Date(date);
          checkDate.setHours(0, 0, 0, 0);
          return leavePeriods.some((period) => {
            const start = new Date(period.startDate);
            const end = period.endDate ? new Date(period.endDate) : new Date();
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            return checkDate >= start && checkDate <= end;
          });
        };

        if (memberData.length === 0) {
          // Create empty sheet with headers
          const emptyData = [
            [
              "Date",
              "Points",
              "Telephone Sales Calls",
              "Face to Face",
              "Appointment Made",
              "Appointment Kept",
              "Fact Find",
              "Service Reviews",
              "Social Calls",
              "High Income Wealthy",
              "Referrals",
              "Closing Interview",
              "Sales Close",
            ],
          ];
          const ws = XLSX.utils.aoa_to_sheet(emptyData);
          XLSX.utils.book_append_sheet(
            workbook,
            ws,
            member.name.substring(0, 31)
          ); // Excel sheet name limit
        } else {
          // Transform data for Excel - filter out invalid dates and log them
          const validMemberData = memberData.filter((entry) => {
            const dateValue = entry.entryDate || entry.entry_date;
            if (!dateValue) return false;

            const date = new Date(dateValue);
            if (isNaN(date.getTime())) {
              console.warn(
                "Invalid date found for member",
                member.name,
                dateValue
              );
              return false;
            }
            return true;
          });

          if (validMemberData.length === 0) {
            // All dates were invalid, create empty sheet
            const emptyData = [
              [
                "Date",
                "Points",
                "Telephone Sales Calls",
                "Face to Face",
                "Appointment Made",
                "Appointment Kept",
                "Fact Find",
                "Service Reviews",
                "Social Calls",
                "High Income Wealthy",
                "Referrals",
                "Closing Interview",
                "Sales Close",
              ],
            ];
            const ws = XLSX.utils.aoa_to_sheet(emptyData);
            XLSX.utils.book_append_sheet(
              workbook,
              ws,
              member.name.substring(0, 31)
            );
          } else {
            // Transform data for Excel using valid dates
            const excelData = [
              [
                "Date",
                "Points",
                "Telephone Sales Calls",
                "Face to Face",
                "Appointment Made",
                "Appointment Kept",
                "Fact Find",
                "Service Reviews",
                "Social Calls",
                "High Income Wealthy",
                "Referrals",
                "Closing Interview",
                "Sales Close",
              ],
              ...validMemberData.map((entry) => {
                const dateValue = entry.entryDate || entry.entry_date;
                try {
                  const date = new Date(dateValue);
                  const formattedDate = date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
                  const onLeave = isDateOnLeave(date);
                  
                  if (onLeave) {
                    return [
                      formattedDate,
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                      "Leave",
                    ];
                  }
                  
                  return [
                    formattedDate,
                    entry.points || 0,
                    entry.telephoneSalesCalls || 0,
                    entry.faceToFace || 0,
                    entry.appointmentMade || 0,
                    entry.appointmentKept || 0,
                    entry.factFind || 0,
                    entry.serviceReviews || 0,
                    entry.socialCalls || 0,
                    entry.highIncomeWealthy || 0,
                    entry.referrals || 0,
                    entry.closingInterview || 0,
                    entry.salesClose || 0,
                  ];
                } catch (error) {
                  console.warn(
                    "Error formatting date for Excel:",
                    dateValue,
                    error
                  );
                  return [
                    "Invalid Date",
                    entry.points || 0,
                    entry.telephoneSalesCalls || 0,
                    entry.faceToFace || 0,
                    entry.appointmentMade || 0,
                    entry.appointmentKept || 0,
                    entry.factFind || 0,
                    entry.serviceReviews || 0,
                    entry.socialCalls || 0,
                    entry.highIncomeWealthy || 0,
                    entry.referrals || 0,
                    entry.closingInterview || 0,
                    entry.salesClose || 0,
                  ];
                }
              }),
            ];

            const ws = XLSX.utils.aoa_to_sheet(excelData);
            XLSX.utils.book_append_sheet(
              workbook,
              ws,
              member.name.substring(0, 31)
            ); // Excel sheet name limit
          }
        }
      });

      // Generate Excel file
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${team.team_name}_performance_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Excel file downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading team performance Excel:", error);
      setSnackbar({
        open: true,
        message: "Failed to download Excel file",
        severity: "error",
      });
    }
  };

  // Download team performance data as Excel
  const downloadTeamPerformanceExcel = async () => {
    try {
      // No validation needed - both all teams and specific team downloads are allowed

      let startDateStr, endDateStr;

      // Calculate date range in IST timezone to match database storage
      if (dateRange === "custom") {
        if (!customStartDate || !customEndDate) {
          setSnackbar({
            open: true,
            message: "Please select both start and end dates for custom range",
            severity: "warning",
          });
          return;
        }

        // Validate date objects
        if (
          isNaN(customStartDate.getTime()) ||
          isNaN(customEndDate.getTime())
        ) {
          setSnackbar({
            open: true,
            message: "Invalid custom date range selected",
            severity: "error",
          });
          return;
        }

        if (customStartDate > customEndDate) {
          setSnackbar({
            open: true,
            message: "Start date cannot be after end date",
            severity: "warning",
          });
          return;
        }

        // Convert custom dates to IST timezone date strings
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const startIstTime = new Date(customStartDate.getTime() + istOffset);
        const endIstTime = new Date(customEndDate.getTime() + istOffset);

        startDateStr = startIstTime.toISOString().split("T")[0];
        endDateStr = endIstTime.toISOString().split("T")[0];
      } else {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
        const istTime = new Date(now.getTime() + istOffset);

        endDateStr = istTime.toISOString().split("T")[0];

        const startDateObj = new Date(istTime);
        startDateObj.setDate(startDateObj.getDate() - parseInt(dateRange));
        startDateStr = startDateObj.toISOString().split("T")[0];
      }
      const formatDateLabel = (date) => {
        if (!date) return "";
        const parsed = new Date(date);
        if (Number.isNaN(parsed.getTime())) {
          console.warn("Invalid date in formatDateLabel:", date);
          return "";
        }
        return parsed.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        });
      };
      const formatFullDate = (dateValue) => {
        if (!dateValue) return "";
        const parsed = new Date(dateValue);
        if (Number.isNaN(parsed.getTime())) {
          console.warn("Invalid date in formatFullDate:", dateValue);
          return "";
        }
        return parsed.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      };
      const buildWeekRanges = (start, end) => {
        const ranges = [];
        if (!start || !end) return ranges;
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        if (
          Number.isNaN(startDateObj.getTime()) ||
          Number.isNaN(endDateObj.getTime())
        ) {
          return ranges;
        }
        startDateObj.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);
        
        // Find the Monday of the week containing the start date
        let cursor = new Date(startDateObj);
        const dayOfWeek = cursor.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        cursor.setDate(cursor.getDate() - daysToMonday);
        
        while (cursor <= endDateObj) {
          const weekStart = new Date(cursor);
          const weekEnd = new Date(cursor);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          // Clamp to the actual requested date range
          const displayStart = weekStart < startDateObj ? startDateObj : weekStart;
          const displayEnd = weekEnd > endDateObj ? endDateObj : weekEnd;
          
          ranges.push({ 
            start: new Date(displayStart), 
            end: new Date(displayEnd),
            weekMonday: new Date(weekStart),
            weekSunday: new Date(weekEnd)
          });
          
          // Move cursor to next Monday
          cursor.setDate(cursor.getDate() + 7);
        }
        return ranges;
      };
      const rangeLabel = `${formatDateLabel(startDateStr)} to ${formatDateLabel(
        endDateStr
      )}`;

      let response;
      let fileName;
      let successMessage;
      const memberMetrics = [
        {
          metric: "telephoneSalesCalls",
          displayName: "Telephone Sales Calls",
          points: 1,
        },
        { metric: "faceToFace", displayName: "Face to Face", points: 1 },
        {
          metric: "appointmentMade",
          displayName: "Appointment Scheduled",
          points: 2,
        },
        {
          metric: "appointmentKept",
          displayName: "Appointment Attended",
          points: 2,
        },
        { metric: "factFind", displayName: "Fact Find", points: 2 },
        {
          metric: "serviceReviews",
          displayName: "Service Reviews",
          points: 1,
        },
        { metric: "socialCalls", displayName: "Social Calls", points: 1 },
        {
          metric: "highIncomeWealthy",
          displayName: "High Income/Wealthy",
          points: 2,
        },
        { metric: "referrals", displayName: "Referrals", points: 3 },
        {
          metric: "closingInterview",
          displayName: "Closing Interview",
          points: 4,
        },
        { metric: "salesClose", displayName: "Sales Close", points: 5 },
      ];
      const dayOrder = [
        { key: "Monday", label: "MON" },
        { key: "Tuesday", label: "TUES" },
        { key: "Wednesday", label: "WED" },
        { key: "Thursday", label: "THURS" },
        { key: "Friday", label: "FRI" },
        { key: "Saturday", label: "SAT" },
        { key: "Sunday", label: "SUN" },
      ];
      const weekdayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const weeklyHeader = [
        "TASK",
        "PTS",
        "MON",
        "TUES",
        "WED",
        "THURS",
        "FRI",
        "SAT",
        "SUN",
        "WK TOTAL",
      ];
      const gapWidth = 2;
      const gapArray = new Array(gapWidth).fill("");
      const createDayTotals = () => {
        const totals = {};
        dayOrder.forEach((day) => {
          totals[day.key] = 0;
        });
        return totals;
      };
      // Helper function to check if a date falls within any leave period
      const isDateOnLeaveForMember = (date, leavePeriods) => {
        if (!leavePeriods || leavePeriods.length === 0) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return leavePeriods.some((period) => {
          const start = new Date(period.startDate);
          const end = period.endDate ? new Date(period.endDate) : new Date();
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return checkDate >= start && checkDate <= end;
        });
      };

      const buildMemberWeekTables = (member, effectiveWeeks) => {
        const leavePeriods = member.leavePeriods || [];
        const localDayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        const localWeeklyHeader = ["TASK", "PTS", "MON", "TUES", "WED", "THURS", "FRI", "SAT", "SUN", "WK TOTAL"];
        
        const weekTables = effectiveWeeks.map((week, index) => {
          const totals = {};
          const leaveDays = {}; // Track which days are on leave
          const dataAvailable = {}; // Track which days have data in the selected range
          
          memberMetrics.forEach((metric) => {
            totals[metric.metric] = {
              Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0,
              Friday: 0, Saturday: 0, Sunday: 0,
            };
          });

          // Use the actual calendar week (weekMonday to weekSunday) for day mapping
          const weekMonday = week.weekMonday || week.start;
          const weekSunday = week.weekSunday || week.end;
          
          // Check each day of the calendar week for leave status and data availability
          let cursor = new Date(weekMonday);
          while (cursor <= weekSunday) {
            const dayName = weekdayNames[cursor.getDay()];
            if (dayName) {
              // Check if this date is within the user's selected range
              const isInRange = cursor >= week.start && cursor <= week.end;
              dataAvailable[dayName] = isInRange;
              
              if (isDateOnLeaveForMember(cursor, leavePeriods)) {
                leaveDays[dayName] = true;
              }
            }
            cursor.setDate(cursor.getDate() + 1);
          }

          // Populate performance data
          (member.performance || []).forEach((entry) => {
            const entryDateValue = entry.entryDate || entry.entry_date;
            if (!entryDateValue) return;
            const entryDate = new Date(entryDateValue);
            if (
              Number.isNaN(entryDate.getTime()) ||
              entryDate < week.start ||
              entryDate > week.end
            ) {
              return;
            }
            const dayName = weekdayNames[entryDate.getDay()];
            if (!dayName) return;
            memberMetrics.forEach((metric) => {
              const value = Number(entry[metric.metric]) || 0;
              totals[metric.metric][dayName] += value;
            });
          });

          const rows = [];
          rows.push([...localWeeklyHeader]);
          
          memberMetrics.forEach((metric) => {
            const metricTotals = totals[metric.metric];
            // Calculate week total (only counting non-leave days that are in range)
            const weekTotal = localDayOrder.reduce(
              (sum, day) => sum + (leaveDays[day] || !dataAvailable[day] ? 0 : (metricTotals[day] || 0)),
              0
            );
            rows.push([
              metric.displayName,
              metric.points,
              ...localDayOrder.map((day) => {
                if (!dataAvailable[day]) return "-"; // Day not in selected range
                if (leaveDays[day]) return "Leave";
                return metricTotals[day] || 0;
              }),
              weekTotal,
            ]);
          });

          // Total Points row
          const totalPointsByDay = localDayOrder.map((day) => {
            if (!dataAvailable[day]) return "-";
            if (leaveDays[day]) return "Leave";
            return memberMetrics.reduce(
              (sum, metric) =>
                sum + (totals[metric.metric][day] || 0) * (metric.points || 0),
              0
            );
          });
          // Calculate memberWeekTotal excluding leave days and days not in range
          const memberWeekTotal = localDayOrder.reduce(
            (sum, day, idx) => {
              if (!dataAvailable[day] || leaveDays[day]) return sum;
              const val = totalPointsByDay[idx];
              return sum + (typeof val === 'number' ? val : 0);
            },
            0
          );
          rows.push(["Total Points", "", ...totalPointsByDay, memberWeekTotal]);

          return {
            label: `Week ${index + 1} (${formatFullDate(
              week.start
            )} - ${formatFullDate(week.end)})`,
            rows,
          };
        });

        const weekLabelRow = [];
        weekTables.forEach((table, index) => {
          weekLabelRow.push(table.label);
          for (let i = 1; i < localWeeklyHeader.length; i += 1) {
            weekLabelRow.push("");
          }
          if (index < weekTables.length - 1) {
            weekLabelRow.push(...gapArray);
          }
        });

        const tableHeight = weekTables[0]?.rows.length || 0;
        const combinedRows = [];
        for (let rowIndex = 0; rowIndex < tableHeight; rowIndex += 1) {
          const row = [];
          weekTables.forEach((table, tableIndex) => {
            row.push(...table.rows[rowIndex]);
            if (tableIndex < weekTables.length - 1) {
              row.push(...gapArray);
            }
          });
          combinedRows.push(row);
        }

        return { weekLabelRow, tableRows: combinedRows };
      };
      const appendMemberWeeklyTables = (
        sheetRows,
        member,
        effectiveWeeks,
        fallbackStart,
        fallbackEnd
      ) => {
        const resolvedWeeks =
          effectiveWeeks.length > 0
            ? effectiveWeeks
            : buildWeekRanges(fallbackStart, fallbackEnd);
        const { weekLabelRow, tableRows } = buildMemberWeekTables(
          member,
          resolvedWeeks
        );
        const memberId = member.user_id || member.userId || member.id || "-";
        sheetRows.push([
          `User: ${member.name || "Unknown"} (User ID: ${memberId})`,
        ]);
        sheetRows.push([]);
        sheetRows.push(weekLabelRow);
        tableRows.forEach((row) => sheetRows.push(row));
        sheetRows.push([]);
      };

      if (selectedTeamFilter === "all") {
        response = await api.get("/api/teams/performance-data/all", {
          params: { startDate: startDateStr, endDate: endDateStr },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        });
        fileName = `all_teams_report_${startDateStr}_to_${endDateStr}.xlsx`;
        successMessage = "All teams Excel file downloaded successfully";
      } else {
        response = await api.get(
          `/api/teams/${selectedTeamFilter}/performance-data`,
          {
            params: { startDate: startDateStr, endDate: endDateStr },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );
        const teamName =
          teams.find((t) => t.team_id.toString() === selectedTeamFilter)
            ?.team_name || "team";
        fileName = `${teamName}_performance_${startDateStr}_to_${endDateStr}.xlsx`;
        successMessage = "Team Excel file downloaded successfully";
      }

      const responseData = response.data || {};
      const teamsData = responseData.teams || [];
      const workbook = XLSX.utils.book_new();

      if (selectedTeamFilter === "all") {
        if (teamsData.length === 0) {
          setSnackbar({
            open: true,
            message: "No performance data available",
            severity: "warning",
          });
          return;
        }

        const rangeInfo = responseData.dateRange || {
          startDate: startDateStr,
          endDate: endDateStr,
        };
        const rangeStart = rangeInfo.startDate || startDateStr;
        const rangeEnd = rangeInfo.endDate || endDateStr;
        fileName = `all_teams_report_${rangeStart}_to_${rangeEnd}.xlsx`;
        const computedWeeks = buildWeekRanges(rangeStart, rangeEnd);
        const weeksToUse = computedWeeks.length
          ? computedWeeks
          : buildWeekRanges(startDateStr, endDateStr);

        teamsData.forEach((team) => {
          const sheetRows = [];
          const teamMembers = team.members || [];

          if (teamMembers.length === 0) {
            sheetRows.push([
              `${team.teamName || "Team"} - No member data available`,
            ]);
          } else {
            teamMembers.forEach((member, memberIndex) => {
              if (memberIndex > 0) {
                sheetRows.push([]);
                sheetRows.push([]);
              }
              appendMemberWeeklyTables(
                sheetRows,
                member,
                weeksToUse,
                rangeStart,
                rangeEnd
              );
            });
          }

          const safeSheetName = `Team_${team.teamName || "Team"}`
            .replace(/[/\\?*\[\]:]/g, "_")
            .slice(0, 31);
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.aoa_to_sheet(sheetRows),
            safeSheetName || "Team"
          );
        });
      } else {
        const teamName =
          teams.find((t) => t.team_id.toString() === selectedTeamFilter)
            ?.team_name || "Team";
        const { members = [] } = response.data;

        if (members.length === 0) {
          setSnackbar({
            open: true,
            message: "No members found in this team.",
            severity: "warning",
          });
          return;
        }

        const computedWeeks = buildWeekRanges(startDateStr, endDateStr);
        const weeksToUse =
          computedWeeks.length > 0
            ? computedWeeks
            : buildWeekRanges(startDateStr, endDateStr);
        const sheetRows = [];

        members.forEach((member, index) => {
          if (index > 0) {
            sheetRows.push([]);
            sheetRows.push([]);
          }
          appendMemberWeeklyTables(
            sheetRows,
            member,
            weeksToUse,
            startDateStr,
            endDateStr
          );
        });

        const safeSheetName = `Team_${teamName}`
          .replace(/[/\\?*\[\]:]/g, "_")
          .slice(0, 31);
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(sheetRows),
          safeSheetName || "Team"
        );
      }
      // Generate Excel file
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Excel file downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading team performance Excel:", error);
      setSnackbar({
        open: true,
        message: "Failed to download Excel file",
        severity: "error",
      });
    }
  };

  // Download team performance data as Excel

  if (loading) {
    return (
      <Box className="loading-container">
        <Typography variant="h4">Team Management</Typography>
        <Typography>Loading teams...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="error-container">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const drawerWidth = 320;

  return (
    <Box className="team-management-container">
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={sidebarOpen}
        className="team-management-sidebar"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            top: "64px",
            height: "calc(100vh - 64px)",
          },
        }}
      >
        <Box className="sidebar-content">
          <Typography variant="h6" className="sidebar-title">
            <GroupIcon />
            Team Actions
          </Typography>
          <Divider className="sidebar-divider" />

          <Button
            className="create-team-button"
            startIcon={<AddIcon />}
            onClick={() => setCreateTeamDialog(true)}
          >
            Create Team
          </Button>

          <Divider className="sidebar-divider" />

          <Typography variant="subtitle2" className="sidebar-subtitle">
            View Options
          </Typography>

          <Button
            className={`view-toggle-button ${
              viewMode === "all" ? "active" : ""
            }`}
            startIcon={<ViewListIcon />}
            onClick={() => setViewMode("all")}
            variant={viewMode === "all" ? "contained" : "outlined"}
            size="small"
          >
            All Teams
          </Button>

          <Button
            className={`view-toggle-button ${
              viewMode === "by-reports" ? "active" : ""
            }`}
            startIcon={<AssessmentIcon />}
            onClick={() => setViewMode("by-reports")}
            variant={viewMode === "by-reports" ? "contained" : "outlined"}
            size="small"
          >
            Teams by Reports
          </Button>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        className={`team-management-main ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <Container maxWidth="lg">
          <Box className="team-management-header">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin-dashboard")}
                variant="outlined"
                sx={{
                  color: "var(--primary-purple)",
                  borderColor: "var(--primary-purple)",
                  "&:hover": {
                    backgroundColor: "rgba(156, 39, 176, 0.1)",
                    borderColor: "var(--primary-purple)",
                  },
                }}
              >
                Back to Dashboard
              </Button>
              <Typography variant="h4" className="team-management-title">
                <GroupIcon />
                Team Management
              </Typography>
            </Box>
          </Box>

          {viewMode === "all" ? (
            <Box className="teams-table-container">
              <TableContainer component={Paper} className="teams-table-wrapper">
                <Table className="teams-table">
                  <TableHead>
                    <TableRow className="teams-table-header">
                      <TableCell className="table-header-cell">
                        Team Name
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Description
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Members
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Created By
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Created Date
                      </TableCell>
                      <TableCell className="table-header-cell" align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow
                        key={team.team_id}
                        className="teams-table-row"
                        hover
                      >
                        <TableCell className="table-cell team-name-cell">
                          <Typography
                            variant="subtitle1"
                            className="team-name-text"
                          >
                            {team.team_name}
                          </Typography>
                        </TableCell>
                        <TableCell className="table-cell">
                          <Typography
                            variant="body2"
                            className="team-description-text"
                          >
                            {team.description || "No description"}
                          </Typography>
                        </TableCell>
                        <TableCell className="table-cell">
                          <Chip
                            label={`${team.member_count} members`}
                            className="member-count-chip"
                            size="small"
                          />
                        </TableCell>
                        <TableCell className="table-cell">
                          <Typography variant="body2" className="creator-text">
                            {team.created_by_name}
                          </Typography>
                        </TableCell>
                        <TableCell className="table-cell">
                          <Typography variant="body2" className="date-text">
                            {new Date(team.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className="table-cell actions-cell"
                          align="center"
                        >
                          <Box className="table-actions">
                            <Tooltip title="Add Members">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openAddMemberDialog(team)}
                                disabled={availableUsers.length === 0}
                              >
                                <PersonAddIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Members">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => openViewMembersDialog(team)}
                              >
                                <GroupIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Performance Excel">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => downloadTeamMembersExcel(team)}
                                  disabled={team.member_count === 0}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit Team">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => openEditDialog(team)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Team">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteTeam(team.team_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {teams.length === 0 && (
                <Box className="no-teams-table-message">
                  <Typography variant="h6">
                    No teams created yet. Create your first team to get started!
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box className="team-performance-container">
              {/* Filters */}
              <Box className="performance-filters">
                <Box className="filter-row">
                  <TextField
                    select
                    label="Select Team"
                    value={selectedTeamFilter}
                    onChange={(e) => setSelectedTeamFilter(e.target.value)}
                    className="team-filter-select"
                    size="small"
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="all">All Teams</MenuItem>
                    {teams.map((team) => (
                      <MenuItem
                        key={team.team_id}
                        value={team.team_id.toString()}
                      >
                        {team.team_name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Date Range"
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="date-filter-select"
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="custom">Custom Range</MenuItem>
                    <MenuItem value="1">1 Day</MenuItem>
                    <MenuItem value="7">7 Days</MenuItem>
                    <MenuItem value="30">30 Days</MenuItem>
                    <MenuItem value="90">90 Days</MenuItem>
                    <MenuItem value="180">6 Months</MenuItem>
                  </TextField>

                  {/* Custom Date Pickers */}
                  {dateRange === "custom" && (
                    <>
                      <TextField
                        type="date"
                        label="Start Date"
                        value={
                          customStartDate
                            ? customStartDate.toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const date = e.target.value
                            ? new Date(e.target.value)
                            : null;
                          setCustomStartDate(date);
                        }}
                        size="small"
                        sx={{ minWidth: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        value={
                          customEndDate
                            ? customEndDate.toISOString().split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const date = e.target.value
                            ? new Date(e.target.value)
                            : null;
                          setCustomEndDate(date);
                        }}
                        size="small"
                        sx={{ minWidth: 150 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </>
                  )}
                </Box>
              </Box>

              {/* Display Mode Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 2,
                  justifyContent: "center",
                }}
              >
                <Button
                  variant={displayMode === "count" ? "contained" : "outlined"}
                  onClick={() => setDisplayMode("count")}
                  sx={{
                    minWidth: 120,
                    backgroundColor:
                      displayMode === "count"
                        ? "var(--primary-purple)"
                        : "transparent",
                    color:
                      displayMode === "count"
                        ? "white"
                        : "var(--primary-purple)",
                    borderColor: "var(--primary-purple)",
                    "&:hover": {
                      backgroundColor:
                        displayMode === "count"
                          ? "var(--primary-purple)"
                          : "rgba(156, 39, 176, 0.1)",
                    },
                  }}
                >
                  Show by Count
                </Button>
                <Button
                  variant={displayMode === "points" ? "contained" : "outlined"}
                  onClick={() => setDisplayMode("points")}
                  sx={{
                    minWidth: 120,
                    backgroundColor:
                      displayMode === "points"
                        ? "var(--primary-purple)"
                        : "transparent",
                    color:
                      displayMode === "points"
                        ? "white"
                        : "var(--primary-purple)",
                    borderColor: "var(--primary-purple)",
                    "&:hover": {
                      backgroundColor:
                        displayMode === "points"
                          ? "var(--primary-purple)"
                          : "rgba(156, 39, 176, 0.1)",
                    },
                  }}
                >
                  Show by Points
                </Button>
              </Box>

              {/* Performance Table */}
              <TableContainer
                component={Paper}
                className="performance-table-wrapper"
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "var(--primary-purple)", fontWeight: 600 }}
                  >
                    Team Performance Data
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip
                      title={
                        selectedTeamFilter === "all"
                          ? "Download All Teams Excel"
                          : "Download Team Excel"
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          onClick={downloadTeamPerformanceExcel}
                          startIcon={<DownloadIcon />}
                          sx={{
                            color: "var(--primary-purple)",
                            borderColor: "var(--primary-purple)",
                            "&:hover": {
                              backgroundColor: "rgba(156, 39, 176, 0.1)",
                              borderColor: "var(--primary-purple)",
                            },
                          }}
                        >
                          {selectedTeamFilter === "all"
                            ? "Download All Teams Excel"
                            : "Download Team Excel"}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      onClick={downloadTeamPerformanceCSV}
                      disabled={performanceData.length === 0}
                      startIcon={<DownloadIcon />}
                      sx={{
                        color: "var(--primary-purple)",
                        borderColor: "var(--primary-purple)",
                        "&:hover": {
                          backgroundColor: "rgba(156, 39, 176, 0.1)",
                          borderColor: "var(--primary-purple)",
                        },
                      }}
                    >
                      Download CSV
                    </Button>
                  </Box>
                </Box>
                <Table className="performance-table">
                  <TableHead>
                    <TableRow className="performance-table-header">
                      <TableCell className="table-header-cell">
                        Metrics
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Points
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Monday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Tuesday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Wednesday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Thursday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Friday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Saturday
                      </TableCell>
                      <TableCell className="table-header-cell">
                        Sunday
                      </TableCell>
                      <TableCell className="table-header-cell">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceData.length > 0 ? (
                      performanceData.map((row, index) => (
                        <TableRow
                          key={row.metric}
                          className={`performance-table-row ${
                            row.metric === "Total Points" ? "total-row" : ""
                          }`}
                          hover
                        >
                          <TableCell
                            className={`table-cell metric-name-cell ${
                              row.metric === "Total Points"
                                ? "total-metric-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "subtitle2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 600,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "telephoneSalesCalls"
                                ? "Telephone Sales Calls"
                                : row.metric === "faceToFace"
                                ? "Face to Face"
                                : row.metric === "appointmentMade"
                                ? "Appointment Scheduled"
                                : row.metric === "appointmentKept"
                                ? "Appointment Attended"
                                : row.metric === "factFind"
                                ? "Fact Find"
                                : row.metric === "serviceReviews"
                                ? "Service Reviews"
                                : row.metric === "socialCalls"
                                ? "Social Calls"
                                : row.metric === "highIncomeWealthy"
                                ? "High Income/Wealthy"
                                : row.metric === "referrals"
                                ? "Referrals"
                                : row.metric === "closingInterview"
                                ? "Closing Interview"
                                : row.metric === "salesClose"
                                ? "Sales Close"
                                : row.metric === "Total Points"
                                ? "Total Points"
                                : row.metric}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {pointValues[row.metricKey] ?? 1}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Monday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Monday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Tuesday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Tuesday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Wednesday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Wednesday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Thursday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Thursday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Friday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Friday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Saturday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Saturday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "subtitle1"
                                  : "body2"
                              }
                              sx={{
                                fontWeight:
                                  row.metric === "Total Points" ? 700 : 400,
                                color:
                                  row.metric === "Total Points"
                                    ? "var(--primary-purple)"
                                    : "inherit",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.Sunday || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.Sunday || 0
                                  )}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={`table-cell metric-cell total-cell ${
                              row.metric === "Total Points"
                                ? "total-points-cell"
                                : ""
                            }`}
                          >
                            <Typography
                              variant={
                                row.metric === "Total Points"
                                  ? "h6"
                                  : "subtitle2"
                              }
                              sx={{
                                fontWeight: 600,
                                color: "var(--primary-purple)",
                              }}
                            >
                              {row.metric === "Total Points"
                                ? performanceData
                                    .filter((r) => r.metric !== "Total Points")
                                    .reduce(
                                      (sum, r) =>
                                        sum +
                                        getDisplayValue(
                                          r.metricKey,
                                          r.total || 0
                                        ),
                                      0
                                    )
                                : getDisplayValue(
                                    row.metricKey,
                                    row.total || 0
                                  )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="table-cell no-data-cell"
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            {selectedTeamFilter === "all"
                              ? "No performance data available for all teams in the selected date range"
                              : "No performance data available for the selected team and date range"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Individual Member Performance Table - Only show when a specific team is selected */}
              {selectedTeamFilter !== "all" && teamMembers.length > 0 && (
                <Box className="member-performance-container" sx={{ mt: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      color: "var(--primary-purple)",
                      fontWeight: 600,
                    }}
                  >
                    Individual Member Performance
                  </Typography>

                  {/* Member Filters */}
                  <Box className="performance-filters" sx={{ mb: 2 }}>
                    <Box className="filter-row">
                      <TextField
                        select
                        label="Select Team Member"
                        value={selectedMemberFilter}
                        onChange={(e) =>
                          setSelectedMemberFilter(e.target.value)
                        }
                        className="member-filter-select"
                        size="small"
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="all">All Members</MenuItem>
                        {teamMembers.map((member) => (
                          <MenuItem
                            key={member.user_id}
                            value={member.user_id.toString()}
                          >
                            {member.name} ({safeFormatRole(member.role)})
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        label="Date Range"
                        value={memberDateRange}
                        onChange={(e) => setMemberDateRange(e.target.value)}
                        className="date-filter-select"
                        size="small"
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="custom">Custom Range</MenuItem>
                        <MenuItem value="1">1 Day</MenuItem>
                        <MenuItem value="7">7 Days</MenuItem>
                        <MenuItem value="30">30 Days</MenuItem>
                        <MenuItem value="90">90 Days</MenuItem>
                        <MenuItem value="180">6 Months</MenuItem>
                      </TextField>

                      {/* Custom Member Date Pickers */}
                      {memberDateRange === "custom" && (
                        <>
                          <TextField
                            type="date"
                            label="Start Date"
                            value={
                              customMemberStartDate
                                ? customMemberStartDate
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                            }
                            onChange={(e) => {
                              const date = e.target.value
                                ? new Date(e.target.value)
                                : null;
                              setCustomMemberStartDate(date);
                            }}
                            size="small"
                            sx={{ minWidth: 150 }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            type="date"
                            label="End Date"
                            value={
                              customMemberEndDate
                                ? customMemberEndDate
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                            }
                            onChange={(e) => {
                              const date = e.target.value
                                ? new Date(e.target.value)
                                : null;
                              setCustomMemberEndDate(date);
                            }}
                            size="small"
                            sx={{ minWidth: 150 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Member Performance Table */}
                  <TableContainer
                    component={Paper}
                    className="performance-table-wrapper"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: "var(--primary-purple)", fontWeight: 600 }}
                      >
                        Individual Member Performance Data
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Download Excel (Weekly Format)">
                          <span>
                            <IconButton
                              onClick={downloadMemberPerformanceExcel}
                              disabled={memberPerformanceData.length === 0}
                              sx={{ color: "var(--primary-purple)" }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Table className="performance-table">
                      <TableHead>
                        <TableRow className="performance-table-header">
                          <TableCell className="table-header-cell">
                            Metrics
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Points
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Monday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Tuesday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Wednesday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Thursday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Friday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Saturday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Sunday
                          </TableCell>
                          <TableCell className="table-header-cell">
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {memberPerformanceData.length > 0 ? (
                          memberPerformanceData.map((row, index) => (
                            <TableRow
                              key={row.metric}
                              className={`performance-table-row ${
                                row.metric === "Total Points" ? "total-row" : ""
                              }`}
                              hover
                            >
                              <TableCell
                                className={`table-cell metric-name-cell ${
                                  row.metric === "Total Points"
                                    ? "total-metric-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "subtitle2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 600,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "telephoneSalesCalls"
                                    ? "Telephone Sales Calls"
                                    : row.metric === "faceToFace"
                                    ? "Face to Face"
                                    : row.metric === "appointmentMade"
                                    ? "Appointment Scheduled"
                                    : row.metric === "appointmentKept"
                                    ? "Appointment Attended"
                                    : row.metric === "factFind"
                                    ? "Fact Find"
                                    : row.metric === "serviceReviews"
                                    ? "Service Reviews"
                                    : row.metric === "socialCalls"
                                    ? "Social Calls"
                                    : row.metric === "highIncomeWealthy"
                                    ? "High Income/Wealthy"
                                    : row.metric === "referrals"
                                    ? "Referrals"
                                    : row.metric === "closingInterview"
                                    ? "Closing Interview"
                                    : row.metric === "salesClose"
                                    ? "Sales Close"
                                    : row.metric === "Total Points"
                                    ? "Total Points"
                                    : row.metric}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {pointValues[row.metricKey] || 1}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Monday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Monday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Monday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Tuesday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Tuesday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Tuesday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Wednesday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Wednesday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Wednesday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Thursday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Thursday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Thursday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Friday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Friday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Friday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Saturday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Saturday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Saturday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "subtitle1"
                                      : "body2"
                                  }
                                  sx={{
                                    fontWeight:
                                      row.metric === "Total Points" ? 700 : 400,
                                    color:
                                      row.metric === "Total Points"
                                        ? "var(--primary-purple)"
                                        : "inherit",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Sum all metrics for Sunday, using current display mode
                                        return memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.Sunday || 0
                                              ),
                                            0
                                          );
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.Sunday || 0
                                      )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                className={`table-cell metric-cell total-cell ${
                                  row.metric === "Total Points"
                                    ? "total-points-cell"
                                    : ""
                                }`}
                              >
                                <Typography
                                  variant={
                                    row.metric === "Total Points"
                                      ? "h6"
                                      : "subtitle2"
                                  }
                                  sx={{
                                    fontWeight: 600,
                                    color: "var(--primary-purple)",
                                  }}
                                >
                                  {row.metric === "Total Points"
                                    ? (() => {
                                        // Calculate sum of all Total values from individual metric rows
                                        const totalSum = memberPerformanceData
                                          .filter(
                                            (r) => r.metric !== "Total Points"
                                          )
                                          .reduce(
                                            (sum, r) =>
                                              sum +
                                              getDisplayValue(
                                                r.metric,
                                                r.total || 0
                                              ),
                                            0
                                          );
                                        return totalSum;
                                      })()
                                    : getDisplayValue(
                                        row.metric,
                                        row.total || 0
                                      )}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              className="table-cell no-data-cell"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                              >
                                {selectedMemberFilter === "all"
                                  ? "Select a specific team member to view their individual performance data"
                                  : "No performance data available for the selected member and date range"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {teams.length === 0 && (
                <Box className="no-performance-message">
                  <Typography variant="h6">
                    No teams available for performance tracking.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {teams.length === 0 && (
            <Box className="no-teams-message">
              <Typography variant="h6">
                No teams created yet. Create your first team to get started!
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Create Team Dialog */}
      <Dialog
        open={createTeamDialog}
        onClose={() => setCreateTeamDialog(false)}
        maxWidth="sm"
        fullWidth
        className="team-dialog"
      >
        <DialogTitle className="team-dialog-title">Create New Team</DialogTitle>
        <DialogContent className="team-dialog-content">
          <TextField
            autoFocus
            className="team-form-field"
            label="Team Name"
            fullWidth
            value={teamForm.team_name}
            onChange={(e) =>
              setTeamForm({ ...teamForm, team_name: e.target.value })
            }
            required
          />
          <TextField
            className="team-form-field"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={teamForm.description}
            onChange={(e) =>
              setTeamForm({ ...teamForm, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions className="team-dialog-actions">
          <Button onClick={() => setCreateTeamDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTeam}
            variant="contained"
            disabled={!teamForm.team_name.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog
        open={editTeamDialog}
        onClose={() => setEditTeamDialog(false)}
        maxWidth="sm"
        fullWidth
        className="team-dialog"
      >
        <DialogTitle className="team-dialog-title">Edit Team</DialogTitle>
        <DialogContent className="team-dialog-content">
          <TextField
            autoFocus
            className="team-form-field"
            label="Team Name"
            fullWidth
            value={teamForm.team_name}
            onChange={(e) =>
              setTeamForm({ ...teamForm, team_name: e.target.value })
            }
            required
          />
          <TextField
            className="team-form-field"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={teamForm.description}
            onChange={(e) =>
              setTeamForm({ ...teamForm, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions className="team-dialog-actions">
          <Button onClick={() => setEditTeamDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditTeam}
            variant="contained"
            disabled={!teamForm.team_name.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog
        open={addMemberDialog}
        onClose={() => setAddMemberDialog(false)}
        maxWidth="md"
        fullWidth
        className="team-dialog"
      >
        <DialogTitle className="team-dialog-title">
          Add Members to {selectedTeam?.team_name}
        </DialogTitle>
        <DialogContent className="team-dialog-content">
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select users to add to this team:
          </Typography>
          <TableContainer
            component={Paper}
            className="team-table"
            sx={{ mt: 2, maxHeight: 400 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Select</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserSelection(user.id)}
                      />
                    </TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.role.replace(/_/g, " ")}</TableCell>
                    <TableCell>{user.location || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {availableUsers.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No available users to add. All users are already assigned to
              teams.
            </Typography>
          )}
        </DialogContent>
        <DialogActions className="team-dialog-actions">
          <Button onClick={() => setAddMemberDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddMembers}
            variant="contained"
            disabled={selectedUsers.length === 0}
          >
            Add Selected Members
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Members Dialog */}
      <Dialog
        open={viewMembersDialog}
        onClose={() => setViewMembersDialog(false)}
        maxWidth="md"
        fullWidth
        className="team-dialog"
      >
        <DialogTitle className="team-dialog-title">
          Team Members - {selectedTeam?.team_name}
        </DialogTitle>
        <DialogContent className="team-dialog-content">
          {selectedTeam?.members && selectedTeam.members.length > 0 ? (
            <TableContainer component={Paper} className="team-table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Role in Team</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedTeam.members.map((member) => (
                    <TableRow key={member.user_id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{safeFormatRole(member.role)}</TableCell>
                      <TableCell>{member.location || "N/A"}</TableCell>
                      <TableCell>{member.role_in_team}</TableCell>
                      <TableCell>
                        {new Date(member.joined_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove from team">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleRemoveMember(
                                selectedTeam.team_id,
                                member.user_id
                              )
                            }
                          >
                            <PersonRemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No members in this team yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions className="team-dialog-actions">
          <Button onClick={() => setViewMembersDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamManagement;
